import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  Public as PlanetIcon,
  Language as GalaxyIcon, // Заменяем Galaxy на Language
  Science as ScienceIcon
} from '@mui/icons-material';

const StarDetails = ({ star, planets = [], galaxies = [] }) => {
  if (!star) return null;

  const starPlanets = planets.filter(planet => 
    planet.star_system_id === star.star_system_id || 
    planet.star_id === star.star_id
  );

  const parentGalaxy = galaxies.find(galaxy => 
    galaxy.galaxy_id === star.galaxy_id
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <ScienceIcon color="primary" sx={{ fontSize: 40 }} />
        <Box>
          <Typography variant="h5" gutterBottom>
            {star.name || 'Unnamed Star'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip label={`Star System: ${star.star_system_id || 'N/A'}`} size="small" />
            <Chip label={`Class: ${star.spectral_class || 'Unknown'}`} size="small" variant="outlined" />
            {star.is_main_sequence && <Chip label="Main Sequence" size="small" color="success" />}
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="Star ID" 
                    secondary={star.star_id || 'N/A'} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Spectral Class" 
                    secondary={star.spectral_class || 'Unknown'} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Mass (Solar Masses)" 
                    secondary={star.mass !== undefined ? star.mass.toFixed(2) : 'Unknown'} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Temperature (K)" 
                    secondary={star.temperature || 'Unknown'} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Luminosity" 
                    secondary={star.luminosity || 'Unknown'} 
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                System Information
              </Typography>
              
              {parentGalaxy && (
                <Box sx={{ mb: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GalaxyIcon fontSize="small" />
                    <Typography variant="body2">
                      Galaxy: {parentGalaxy.name || parentGalaxy.galaxy_id}
                    </Typography>
                  </Box>
                </Box>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PlanetIcon fontSize="small" />
                <Typography variant="body2">
                  Planets in system: {starPlanets.length}
                </Typography>
              </Box>

              {starPlanets.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Planetary System:
                  </Typography>
                  <List dense>
                    {starPlanets.slice(0, 5).map(planet => (
                      <ListItem key={planet.planet_id}>
                        <ListItemText 
                          primary={planet.name || `Planet ${planet.planet_id}`}
                          secondary={`Type: ${planet.planet_type || 'Unknown'}`}
                        />
                      </ListItem>
                    ))}
                    {starPlanets.length > 5 && (
                      <ListItem>
                        <ListItemText 
                          primary={`... and ${starPlanets.length - 5} more planets`}
                        />
                      </ListItem>
                    )}
                  </List>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {star.description && (
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Description
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {star.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default StarDetails;